import { type Driver, GraphQLDriversRequester } from '@cobliteam/api-lib';
import type { BatchCreateDriverItemInput } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { DriverNames, UUID } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { type UseQueryOptions, type UseSuspenseQueryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { trimDriverName } from './utils';

export const QUERY_KEY = 'DRIVERS';
export const QUERY_KEY_WITH_VEHICLES = 'DRIVERS_WITH_VEHICLES';
export const QUERY_KEY_NAMES = 'DRIVER_NAMES';
// Sob a raiz `QUERY_KEY`, para as invalidações da lista também valerem para os ids.
export const QUERY_KEY_IDS = [QUERY_KEY, 'ids'] as const;

type GetDriversParams = { options?: Partial<UseQueryOptions<Driver[]>> } | undefined;
type GetDriverIdsParams = { options?: Partial<UseQueryOptions<string[]>> } | undefined;
type GetDriverNamesParams = { options?: Partial<UseQueryOptions<DriverNames>> } | undefined;

const useDriver = () => {
  const graphqlDriverRequester = new GraphQLDriversRequester();

  const queryClient = useQueryClient();
  const { t } = useTranslation('drivers');

  const useDrivers = (params?: GetDriversParams) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<Driver[]>({
      ...params?.options,
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const useDriversSuspense = (options?: Partial<UseSuspenseQueryOptions<Driver[]>>) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useSuspenseQuery<Driver[]>({
      ...options,
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
      retry: options?.retry ?? 0,
      refetchOnMount: options?.refetchOnMount ?? true,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
      staleTime: options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const useDriversWithVehicles = (params?: GetDriversParams) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<Driver[]>({
      ...params?.options,
      queryKey: [QUERY_KEY_WITH_VEHICLES],
      queryFn: () => graphqlDriverRequester.getDriversWithVehicles(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const useDriverIds = (params?: GetDriverIdsParams) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<string[]>({
      ...params?.options,
      queryKey: QUERY_KEY_IDS,
      queryFn: () => graphqlDriverRequester.getDriverIds(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const useDriverNames = (params?: GetDriverNamesParams) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<DriverNames>({
      ...params?.options,
      queryKey: [QUERY_KEY_NAMES],
      queryFn: () => graphqlDriverRequester.getDriverNames(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const prefetchDrivers = () => {
    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
    });
  };

  const prefetchDriversWithVehicles = () => {
    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY_WITH_VEHICLES],
      queryFn: () => graphqlDriverRequester.getDriversWithVehicles(),
    });
  };

  const useSaveDriver = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<Driver, unknown, Driver>({
      mutationKey: [QUERY_KEY],
      mutationFn: async (driver) => graphqlDriverRequester.saveDriver(trimDriverName(driver)),
      onMutate: async (driver) => {
        await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
        const previousDrivers = queryClient.getQueryData<Driver[]>([QUERY_KEY]);
        queryClient.setQueryData<Driver[]>([QUERY_KEY], (oldDrivers) => {
          if (oldDrivers) return oldDrivers.map((d) => (d.id === driver.id ? driver : d));
        });
        return { previousDrivers };
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        // The drivers table reads `getDriversWithVehicles`, a separate cache key that the optimistic
        // update above never touches. Without this the table keeps serving the pre-save driver, which
        // is most visible with the profile photo: it is written as `profile_image_base64` and comes
        // back as a signed `profile_image_url`, so only a refetch can show the new avatar (or clear a
        // removed one).
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_WITH_VEHICLES] });
        toast.success(t('hooks.save_driver_success'));
      },
      onError: (err: any, variables, context: any) => {
        queryClient.setQueryData([QUERY_KEY], context.previousDrivers);
        toast.error(t('hooks.save_driver_error'));
      },
    });
  };

  const useCreateDriversBatch = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<Driver[], unknown, BatchCreateDriverItemInput[]>({
      mutationFn: async (drivers) => graphqlDriverRequester.createDriversBatch(drivers),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        toast.success(t('sheet_import.import_success'));
      },
    });
  };

  const useDeleteDriver = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<UUID, unknown, UUID>({
      mutationFn: async (id) => graphqlDriverRequester.deleteDriver(id),
      onSuccess: () => {
        toast.success(t('hooks.delete_driver_success'));
        queryClient.resetQueries({ queryKey: [QUERY_KEY] });
        queryClient.resetQueries({ queryKey: ['QUERY_GET_IDENTIFICATIONS_TOKENS_V2_KEY'] });
        queryClient.resetQueries({ queryKey: ['IDENTIFICATION_TOKEN_V2'] });
      },
      onError: () => {
        toast.error(t('hooks.delete_driver_error'));
      },
    });
  };

  return {
    useDrivers,
    useDriverIds,
    useDriversSuspense,
    useDriverNames,
    useDriversWithVehicles,
    useSaveDriver,
    useCreateDriversBatch,
    useDeleteDriver,
    prefetchDrivers,
    prefetchDriversWithVehicles,
  };
};

export default useDriver;
